import { ReactNode } from 'react';
import * as R from 'ramda';
import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { CptbData, DecayData, RoasData, SatData } from '@blisspointmedia/bpm-types/dist/MMM';
import IncrementalityIcon from '@src/assets/IncrementalityIcon.svg?react';
import {
  AreaGrouping,
  AreaItem,
  capitalizeWords,
  currencyFormatter,
  DateChartRow,
  GroupedAreas,
  MedianOrMean,
  numberFormatter
} from '@blisspointmedia/bpm-types/dist/MMMUtils';

const TEST_COMPANY_INCREMENTALITY = [
  'Direct Mail',
  'Paid Search',
  'Paid Search, Brand',
  'Paid Search, Non Brand',
  'Paid Search, Non-brand'
];

const getIncrementality = (
  itemName: string,
  itemIncrementality: boolean,
  disabledPlatform: boolean | undefined,
  companyEqualsTest: boolean | undefined
): boolean =>
  companyEqualsTest
    ? TEST_COMPANY_INCREMENTALITY.includes(capitalizeWords(itemName, disabledPlatform))
    : itemIncrementality;

const normalizeChannelData = <T extends { channel: string }>(data: T[]): T[] =>
  data.map(item => ({
    ...item,
    channel: item.channel?.split(',')[0]?.trim() || item.channel
  }));

interface SnapShotBarItem {
  name: string;
  value: number | null;
  label: string;
  incrementality: boolean;
  labelRawNumber?: number;
  value2?: number;
  label2?: { top: string; bottom: number | string };
  spendOverOutcome?: number | null;
  spend_volume?: number;
  effect_volume?: number;
}

interface SnapShotResult {
  barData: SnapShotBarItem[];
  titleData: string | null;
}

export const cptbSnapShotData = ({
  companyEqualsTest,
  data,
  disabledPlatform,
  groupBy,
  conversionPrettyName,
  type,
  kpiType
}: {
  companyEqualsTest?: boolean;
  data: CptbData[];
  disabledPlatform: boolean | undefined;
  groupBy: string;
  conversionPrettyName: string;
  type: string;
  kpiType: string;
}): SnapShotResult => {
  const sumNumericField = (rows: CptbData[], field: string): number =>
    rows.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);

  let groupedData: Record<string, CptbData[]>;
  switch (groupBy) {
    case 'platform': {
      const normalizedData = normalizeChannelData(data);

      // Group the data by channel and then by platform
      const byChannel = R.groupBy<CptbData>(item => item.channel, normalizedData);
      const chanPlatGrouped: Record<string, Record<string, CptbData[]>> = {};
      Object.keys(byChannel).forEach(channel => {
        chanPlatGrouped[channel] = R.groupBy<CptbData>(item => item.platform, byChannel[channel]);
      });

      // format grouped data to have key as [channel, platform]: data instead of channel: {platform: data}
      const formattedChanPlat: Record<string, CptbData[]> = {};
      Object.keys(chanPlatGrouped).forEach(key => {
        Object.keys(chanPlatGrouped[key]).forEach(platform => {
          formattedChanPlat[`${key}, ${platform}`] = chanPlatGrouped[key][platform];
        });
      });

      groupedData = formattedChanPlat;
      break;
    }
    case 'name': {
      const byChannel = R.groupBy<CptbData>(item => item.channel, data);
      const chanPlatTacBrandGrouped: Record<
        string,
        Record<string, Record<string, Record<string, CptbData[]>>>
      > = {};
      Object.keys(byChannel).forEach(channel => {
        const byPlatform = R.groupBy<CptbData>(item => item.platform, byChannel[channel]);
        chanPlatTacBrandGrouped[channel] = {};
        Object.keys(byPlatform).forEach(platform => {
          const byTactic = R.groupBy<CptbData>(item => item.tactic, byPlatform[platform]);
          chanPlatTacBrandGrouped[channel][platform] = {};
          Object.keys(byTactic).forEach(tactic => {
            chanPlatTacBrandGrouped[channel][platform][tactic] = R.groupBy<CptbData>(
              item => item.brand_nonbrand ?? 'null',
              byTactic[tactic]
            );
          });
        });
      });

      const formattedChanPlatTacBrand: Record<string, CptbData[]> = {};
      Object.keys(chanPlatTacBrandGrouped).forEach(channel => {
        Object.keys(chanPlatTacBrandGrouped[channel]).forEach(platform => {
          Object.keys(chanPlatTacBrandGrouped[channel][platform]).forEach(tactic => {
            Object.keys(chanPlatTacBrandGrouped[channel][platform][tactic]).forEach(brand => {
              const parts = [channel];
              if (platform && platform !== 'null') {
                parts.push(platform);
              }
              if (tactic && tactic !== 'null') {
                parts.push(tactic);
              }
              if (brand && brand !== 'null') {
                parts.push(brand);
              }
              formattedChanPlatTacBrand[parts.join(', ')] =
                chanPlatTacBrandGrouped[channel][platform][tactic][brand];
            });
          });
        });
      });

      groupedData = formattedChanPlatTacBrand;
      break;
    }
    default: {
      const normalizedData = groupBy === 'channel' ? normalizeChannelData(data) : data;
      const byGroupKey = R.groupBy<CptbData>(item => item[groupBy], normalizedData);
      const result: Record<string, CptbData[]> = {};
      Object.keys(byGroupKey).forEach(key => {
        result[key] = byGroupKey[key].map(item => R.omit(['covariates'], item) as CptbData);
      });
      groupedData = result;
      break;
    }
  }

  // Sum of predicted_outcome values by grouping
  const sumOfOutcome = Object.keys(groupedData).map(item => {
    const predictedOutcomeSum = sumNumericField(groupedData[item], 'predicted_outcome');
    const capitalizedName = capitalizeWords(item, disabledPlatform);
    return {
      name: capitalizedName,
      value: predictedOutcomeSum,
      incrementality: Boolean(groupedData[item][0].incrementality_test)
    };
  });
  // Sum of raw_spend values by grouping
  const sumOfSpend = Object.keys(groupedData).map(item => {
    const spendSum = sumNumericField(groupedData[item], 'raw_spend');

    return {
      name: capitalizeWords(item, disabledPlatform),
      value: spendSum
    };
  });

  const sumOfOutcomeAll = sumOfOutcome.reduce((acc, row) => acc + row.value, 0);
  const sumOfSpendAll = sumOfSpend.reduce((acc, row) => acc + row.value, 0);
  const sumOfOutcomeAllSansCovariates =
    sumOfOutcomeAll -
    (sumOfOutcome.find(sum => sum.name.toLowerCase() === 'covariates')?.value ?? 0);

  const overallCPA = sumOfSpendAll / sumOfOutcomeAllSansCovariates;
  const overallROAS = sumOfOutcomeAllSansCovariates / sumOfSpendAll;

  const calcCpaRoasSpend: SnapShotBarItem[] = Object.keys(groupedData).map(item => {
    const rawSpendSum = sumNumericField(groupedData[item], 'raw_spend');
    const predictedOutcomeSum = sumNumericField(groupedData[item], 'predicted_outcome');

    const avg = rawSpendSum / predictedOutcomeSum;
    const spendOverOutcome = Number.isFinite(avg) ? avg : null;
    const outcomeOverSpendAvg = predictedOutcomeSum / rawSpendSum;
    const outcomeOverSpend = Number.isFinite(outcomeOverSpendAvg) ? outcomeOverSpendAvg : null;

    const spendAndEffectShare: SnapShotBarItem = {
      name: capitalizeWords(item, disabledPlatform),
      value: (rawSpendSum / sumOfSpendAll) * 100,
      value2: (predictedOutcomeSum / sumOfOutcomeAllSansCovariates) * 100,
      label: `Spend ${currencyFormatter.format(rawSpendSum)}`,
      label2: {
        top: conversionPrettyName,
        bottom:
          kpiType === 'revenue'
            ? currencyFormatter.format(predictedOutcomeSum)
            : numberFormatter.format(predictedOutcomeSum)
      },
      spendOverOutcome,
      incrementality: getIncrementality(
        item,
        Boolean(groupedData[item][0].incrementality_test),
        disabledPlatform,
        companyEqualsTest
      ),
      spend_volume: rawSpendSum,
      effect_volume: predictedOutcomeSum
    };

    if (type === 'spendAndEffectShare') {
      return spendAndEffectShare;
    }

    return {
      name: capitalizeWords(item, disabledPlatform),
      value: type === 'roas' ? outcomeOverSpend : spendOverOutcome,
      label:
        type === 'roas'
          ? `${((outcomeOverSpend ?? 0) / overallROAS).toFixed(2)} x overall`
          : `${((spendOverOutcome ?? 0) / overallCPA).toFixed(2)} x overall`,
      incrementality: getIncrementality(
        item,
        Boolean(groupedData[item][0].incrementality_test),
        disabledPlatform,
        companyEqualsTest
      ),
      labelRawNumber:
        type === 'roas'
          ? (outcomeOverSpend ?? 0) / overallROAS
          : (spendOverOutcome ?? 0) / overallCPA
    };
  });

  const revenueBarData: SnapShotBarItem[] = sumOfOutcome.map(item => ({
    name: item.name,
    value: (item.value / sumOfOutcomeAll) * 100,
    label:
      kpiType === 'revenue'
        ? currencyFormatter.format(item.value)
        : numberFormatter.format(item.value),
    incrementality: getIncrementality(
      item.name,
      item.incrementality,
      disabledPlatform,
      companyEqualsTest
    ),
    labelRawNumber: item.value
  }));

  switch (type) {
    case 'revenue':
      return { barData: revenueBarData, titleData: null };
    case 'costPerAcquisition':
      return {
        barData: calcCpaRoasSpend,
        titleData: currencyFormatter.format(overallCPA)
      };
    case 'roas':
      return { barData: calcCpaRoasSpend, titleData: overallROAS.toFixed(2) };
    default:
      return {
        barData: calcCpaRoasSpend,
        titleData: `Total spend to effect ratio ${sumOfSpendAll / sumOfSpendAll}:${
          sumOfOutcomeAll / sumOfSpendAll
        }`
      };
  }
};

export interface StageTwoFileData {
  date: string;
  primary_channel: string;
  primary_channel_channel?: string;
  primary_channel_platform?: string;
  primary_channel_tactic?: string;
  primary_channel_brand_nonbrand?: string;
  primary_channel_media_type?: string;
  secondary_channel: string;
  secondary_channel_channel?: string;
  secondary_channel_platform?: string;
  secondary_channel_tactic?: string;
  secondary_channel_brand_nonbrand?: string;
  secondary_channel_media_type?: string;
  value: number;
  cat: string;
  type: string;
  primary_channel_incrementality_test: boolean;
  refresh_date?: string;
}

interface StageTwoGroupedData {
  name: string;
  displayName: string;
  primaryChannelName: string;
  channelName?: string;
  platformName?: string;
  tacticName?: string;
  brandNonBrandName?: string;
  incrementality: boolean;
  added: null | boolean;
  contributors: Set<string>;
  contributorsSpendSum: number; // a total sum of values nest in obj for convenience
  contributorsEffectSum: number;
  totalSpendForChannel: number;
  totalEffectForChannel: number;
  value: number;
  spendRawValue: number;
  effectRawValue: number;
  contributorsSpendSumRawValue: number;
  contributorsEffectSumRawValue: number;
  totalActualizedSpendRawValue: number;
  totalActualizedEffectRawValue: number;
  totalActualizedSpendRawValueLabel: string;
  totalActualizedEffectRawValueLabel: string;
  totalSpendForChannelLabel: string;
  totalEffectForChannelLabel: string;
  value2: number;
  label: string;
  label2: {
    top: string;
    bottom: number | string;
  };
  spendOverOutcome: null | number;
  totalActualizedSpend: number;
  totalActualizedEffect: number;
  // Bar-render-only lengths (gross, unlike totalActualizedSpend/Effect) -- see their
  // computation site below for why.
  totalActualizedSpendBarValue: number;
  totalActualizedEffectBarValue: number;
  // Dynamic contributor aggregate keys: `${secondaryGroupName}:spend`,
  // `${secondaryGroupName}:effect`, plus their `RawValue` variants.
  [contributorKey: string]: any;
}

export const cptbTwoStageSnapData = (
  data: StageTwoFileData[],
  disabledPlatform: boolean | undefined,
  conversionPrettyName: string,
  kpiType: string,
  covariates: boolean,
  groupBy: string,
  // When BE is enabled, Covariates' effect is the residual `beePredictedRevenueTotal -
  // paidAttributionTotal - mdbeInducedEffectTotal` (DEV-21662) instead of a naive cross-model sum
  // -- see the computation site below. Defaults to the BE-off/naive-sum behavior otherwise, so the
  // single-model MMM caller (no BE toggle) is unaffected.
  isBrandEquityEnabled = false,
  beePredictedRevenueTotal = 0,
  paidAttributionTotal = 0
): { barData: StageTwoGroupedData[]; titleData: string } => {
  let totalSpendAllChannels = 0;
  let totalEffectAllChannels = 0;

  const result: Record<string, StageTwoGroupedData> = data.reduce((acc, row) => {
    const {
      cat,
      type,
      value,
      primary_channel,
      primary_channel_channel,
      primary_channel_platform,
      primary_channel_tactic,
      primary_channel_brand_nonbrand,
      primary_channel_media_type,
      primary_channel_incrementality_test,
      secondary_channel,
      secondary_channel_channel,
      secondary_channel_platform,
      secondary_channel_tactic,
      secondary_channel_brand_nonbrand,
      secondary_channel_media_type
    } = row;

    // Newer MMM output files use lowercase, older use uppercase. Handle both.
    const categoryLowercased = cat.toLowerCase();
    const spendOrEffectTypeLowercased = type.toLowerCase();

    // Old-format (legacy R-pipeline) runs use the capitalized "Direct"/"Reduced"/"Added"
    // convention, and have unreliable Direct effect values for at least some demand-capture
    // channels (DEV-24000) -- a channel's "Reduced" (halo effect reallocated away) can be non-zero
    // while its own "Direct" effect is always 0, netting to a nonsensical negative value once the
    // direct-only guard below is applied unconditionally. Old-format rows still flow through the
    // contributors/Sankey-link population below unchanged (so old runs keep their reattribution
    // visualization, per Phil Rundall's DEV-24000 feedback) -- only the totalEffectForChannel
    // accumulation branches by format: old-format sums reduced in too (pre-DEV-23906 behavior,
    // which always cancels back to raw Direct -- never negative, exactly how it behaved before);
    // modern (lowercase) runs use the correct direct-only netting.
    const isLegacyCaseConvention = cat === 'Direct' || cat === 'Reduced' || cat === 'Added';

    // rMMM parity: exclude a row when EITHER primary_channel or
    // primary_channel_channel is "covariates" (matches the original MMM formatter).
    if (
      !covariates &&
      (primary_channel?.toLowerCase() === 'covariates' ||
        primary_channel_channel?.toLowerCase() === 'covariates')
    ) {
      return acc;
    }

    // Make group out of channel/platform/tactic/brandnonbrand
    let primaryGroupName = '';
    let displayName: string;

    // secondaryGroupName must resolve to the SAME name this channel gets when it's primary
    // elsewhere (e.g. Video's own group.name), regardless of which branch primary's own taxonomy
    // sends this row down. It used to be built only inside the primary-has-taxonomy branch below,
    // falling back to a bare, unsuffixed name whenever primary had none (e.g. Media-Driven Brand
    // Equity as primary, a real channel as secondary) -- but SpendAndEffectBarChart looks up
    // contributors by `channel.name`, which always carries the media_type suffix for a real
    // channel. That mismatch left MDBE's own "reduced" contribution keyed under an unsuffixed
    // name ("Video") that never matches the real "Video, Demand Generation" group, so it silently
    // failed to render (DEV-24230 review).
    const secondaryHasFullTaxonomy = Boolean(secondary_channel_channel);
    let secondaryGroupName = '';
    if (secondary_channel_channel) {
      secondaryGroupName = capitalizeWords(secondary_channel_channel, disabledPlatform);
    } else if (secondary_channel) {
      secondaryGroupName = capitalizeWords(secondary_channel, disabledPlatform);
    }
    if (['platform', 'name'].includes(groupBy)) {
      secondaryGroupName += secondary_channel_platform
        ? `, ${capitalizeWords(secondary_channel_platform, disabledPlatform)}`
        : '';
    }
    if (groupBy === 'name') {
      secondaryGroupName += secondary_channel_tactic
        ? `, ${capitalizeWords(secondary_channel_tactic, disabledPlatform)}`
        : '';
      secondaryGroupName += secondary_channel_brand_nonbrand
        ? `, ${capitalizeWords(secondary_channel_brand_nonbrand, disabledPlatform)}`
        : '';
    }
    // Gated separately: MDBE's own primaryGroupName (built via the `else` branch when it's
    // primary) has no suffix, since its media_type is a copy of its own name -- appending it here
    // too would desync the two references to the same entity.
    if (secondaryHasFullTaxonomy) {
      secondaryGroupName += secondary_channel_media_type
        ? `, ${capitalizeWords(secondary_channel_media_type, disabledPlatform)}`
        : '';
    }

    // Group runs generated after 3/21/2025 differently from older runs
    if (
      primary_channel_channel ||
      primary_channel_platform ||
      primary_channel_tactic ||
      primary_channel_brand_nonbrand
    ) {
      if (primary_channel_channel) {
        primaryGroupName += capitalizeWords(primary_channel_channel, disabledPlatform);
      }
      if (['platform', 'name'].includes(groupBy)) {
        primaryGroupName += primary_channel_platform
          ? `, ${capitalizeWords(primary_channel_platform, disabledPlatform)}`
          : '';
      }
      if (groupBy === 'name') {
        primaryGroupName += primary_channel_tactic
          ? `, ${capitalizeWords(primary_channel_tactic, disabledPlatform)}`
          : '';
        primaryGroupName += primary_channel_brand_nonbrand
          ? `, ${capitalizeWords(primary_channel_brand_nonbrand, disabledPlatform)}`
          : '';
      }
      displayName = primaryGroupName;
      primaryGroupName += primary_channel_media_type
        ? `, ${capitalizeWords(primary_channel_media_type, disabledPlatform)}`
        : '';
    } else {
      primaryGroupName = capitalizeWords(primary_channel, disabledPlatform);
      displayName = primaryGroupName;
    }

    // Add primary group(s) to acc
    if (!acc[primaryGroupName]) {
      acc[primaryGroupName] = {
        name: primaryGroupName,
        displayName,
        channelName: primary_channel_channel,
        platformName: primary_channel_platform,
        tacticName: primary_channel_tactic,
        brandNonBrandName: primary_channel_brand_nonbrand,
        incrementality: Boolean(primary_channel_incrementality_test),
        added: null,
        contributors: new Set(),
        contributorsSpendSum: 0,
        contributorsEffectSum: 0,
        totalSpendForChannel: 0,
        totalEffectForChannel: 0,
        value: 0,
        value2: 0,
        label: '',
        label2: {
          top: conversionPrettyName,
          bottom: ''
        },
        spendOverOutcome: null
      };
    }

    if (acc[primaryGroupName].added === null) {
      const mediaType = primary_channel_media_type;
      if (mediaType === 'demand_generation' || mediaType === 'DG') {
        acc[primaryGroupName].added = true;
      } else if (mediaType === 'demand_capture' || mediaType === 'DC') {
        acc[primaryGroupName].added = false;
      } else if (categoryLowercased !== 'direct') {
        acc[primaryGroupName].added = categoryLowercased === 'added';
      }
    }

    if (value === 0) {
      return acc;
    }

    if (categoryLowercased === 'direct' || categoryLowercased === 'reduced') {
      if (spendOrEffectTypeLowercased === 'spend') {
        if (categoryLowercased === 'direct') {
          acc[primaryGroupName].totalSpendForChannel += value;
          totalSpendAllChannels += value;
        }
      } else if (categoryLowercased === 'direct' || isLegacyCaseConvention) {
        acc[primaryGroupName].totalEffectForChannel += value;
        totalEffectAllChannels += value;
      }
    }

    if (categoryLowercased === 'added' || categoryLowercased === 'reduced') {
      if (spendOrEffectTypeLowercased === 'spend') {
        acc[primaryGroupName].contributorsSpendSum += value;
      } else {
        acc[primaryGroupName].contributorsEffectSum += value;
      }
    }

    // Add secondary group(s) to contributors of primary in acc
    if (value === 0) {
      return acc;
    }

    if (categoryLowercased === 'added' || categoryLowercased === 'reduced') {
      if (acc[primaryGroupName]) {
        acc[primaryGroupName].contributors.add(secondaryGroupName);
        acc[primaryGroupName][`${secondaryGroupName}:${spendOrEffectTypeLowercased}`] =
          (acc[primaryGroupName][`${secondaryGroupName}:${spendOrEffectTypeLowercased}`] || 0) +
          value;
      } else {
        console.error(
          `cptbTwoStageSnapData dataFormatter could not find matching group for ${primaryGroupName}`
        );
      }
    }

    return acc;
  }, {});

  // MDBE's total induced effect on real channels (DEV-21662, HROAS/Brand-Equity only -- MMM's
  // caller never sets isBrandEquityEnabled and has no MDBE group, so this is skipped there).
  // Counting Brand Equity's own paid_attribution as "paid media" double-counts revenue already
  // reattributed to real channels via MDBE's "added"/"reduced" rows, so the residual below uses
  // this instead. Each real channel's `${mdbeGroupName}:effect` accumulator already holds MDBE's
  // contribution to it, so summing across every non-Covariates, non-MDBE group gives the total.
  let mdbeInducedEffectTotal = 0;
  if (isBrandEquityEnabled) {
    const mdbeGroupForInducedEffect = Object.values(result).find(
      group => group.name.toLowerCase() === 'media-driven brand equity'
    );
    if (mdbeGroupForInducedEffect) {
      const mdbeEffectKey = `${mdbeGroupForInducedEffect.name}:effect`;
      Object.values(result).forEach(group => {
        if (group === mdbeGroupForInducedEffect || group.name.toLowerCase() === 'covariates') {
          return;
        }
        mdbeInducedEffectTotal += Number(group[mdbeEffectKey]) || 0;
      });
    }
  }

  // Only applied when BE has a positive predicted-revenue total for this window -- otherwise the
  // residual would be meaningless (0 minus a positive sum), so fall back to the naive sum above.
  const covariatesGroup = Object.values(result).find(
    group => group.name.toLowerCase() === 'covariates'
  );
  if (covariatesGroup && isBrandEquityEnabled && beePredictedRevenueTotal > 0) {
    // `totalEffectAllChannels` (the shared denominator every other group's own %/label is
    // computed against, below) is deliberately left untouched, so toggling Brand Equity on/off
    // never shifts other channels' displayed shares.
    covariatesGroup.totalEffectForChannel =
      beePredictedRevenueTotal - paidAttributionTotal - mdbeInducedEffectTotal;
  }

  const resultsToArray = Object.values(result).map(originalGroup => {
    const group: StageTwoGroupedData = {
      ...originalGroup,
      label2: { ...originalGroup.label2 }
    };
    group.spendRawValue =
      group.totalSpendForChannel - (group.added ? 0 : group.contributorsSpendSum);
    if (group.spendRawValue < 0.0005) {
      group.spendRawValue = 0;
    }
    group.value = (group.spendRawValue / totalSpendAllChannels) * 100;
    group.effectRawValue =
      group.totalEffectForChannel - (group.added ? 0 : group.contributorsEffectSum);
    group.value2 = (group.effectRawValue / totalEffectAllChannels) * 100;
    // Covariates' own % uses BE's predicted revenue as its denominator (matching TTM % Credit to
    // Paid Media's percentage basis), not the shared totalEffectAllChannels every other channel
    // uses -- these are two different totals by design, so Covariates' % won't sum to 100% with
    // the rest. Everything derived from value2 below (totalActualizedEffect, the bar-render
    // length) picks this up automatically.
    if (
      group.name.toLowerCase() === 'covariates' &&
      isBrandEquityEnabled &&
      beePredictedRevenueTotal > 0
    ) {
      group.value2 = (group.effectRawValue / beePredictedRevenueTotal) * 100;
    }

    group.contributorsSpendSumRawValue = group.contributorsSpendSum;
    group.contributorsSpendSum = (group.contributorsSpendSum / totalSpendAllChannels) * 100;
    group.contributorsEffectSumRawValue = group.contributorsEffectSum;
    group.contributorsEffectSum = (group.contributorsEffectSum / totalEffectAllChannels) * 100;
    group.label = currencyFormatter.format(group.totalSpendForChannel + group.contributorsSpendSum);
    group.label2.bottom =
      kpiType === 'revenue'
        ? currencyFormatter.format(group.totalEffectForChannel + group.contributorsEffectSum)
        : numberFormatter.format(group.totalEffectForChannel + group.contributorsEffectSum);
    group.spendOverOutcome =
      group.totalEffectForChannel === 0
        ? 0
        : group.totalSpendForChannel / group.totalEffectForChannel;
    group.totalSpendForChannelLabel = currencyFormatter.format(group.totalSpendForChannel);
    group.totalEffectForChannelLabel =
      kpiType === 'revenue'
        ? currencyFormatter.format(group.totalEffectForChannel)
        : numberFormatter.format(group.totalEffectForChannel);

    group.contributors.forEach(contributor => {
      if (group[`${contributor}:spend`]) {
        const spendContribution = (group[`${contributor}:spend`] / totalSpendAllChannels) * 100;
        group[`${contributor}:spendRawValue`] = group[`${contributor}:spend`];
        group[`${contributor}:spend`] = spendContribution;
      }
      if (group[`${contributor}:effect`]) {
        const effectContribution = (group[`${contributor}:effect`] / totalEffectAllChannels) * 100;
        group[`${contributor}:effectRawValue`] = group[`${contributor}:effect`];
        group[`${contributor}:effect`] = effectContribution;
      }
    });
    if (group.added === null) {
      group.added = false;
    }
    group.totalActualizedSpend = group.added
      ? group.value + group.contributorsSpendSum
      : group.value;
    group.totalActualizedSpendRawValue = group.added
      ? group.spendRawValue + group.contributorsSpendSumRawValue
      : group.spendRawValue;
    group.totalActualizedSpendRawValueLabel = currencyFormatter.format(
      group.totalActualizedSpendRawValue
    );
    group.totalActualizedEffect = group.added
      ? group.value2 + group.contributorsEffectSum
      : group.value2;
    group.totalActualizedEffectRawValue = group.added
      ? group.effectRawValue + group.contributorsEffectSumRawValue
      : group.effectRawValue;
    group.totalActualizedEffectRawValueLabel =
      kpiType === 'revenue'
        ? currencyFormatter.format(group.totalActualizedEffectRawValue)
        : numberFormatter.format(group.totalActualizedEffectRawValue);

    // Media-Driven Brand Equity's "Actualized" (net) figure nets to ~0 by construction (direct
    // and reduced are the same underlying number), rendering as a garbled dash/"<1%" instead of
    // "0" (DEV-21662). Hard-code only Actualized to $0 -- leave `value2`/`contributorsEffectSum`/
    // `totalEffectForChannel`/`totalActualizedEffectBarValue` untouched so the real "Direct"
    // breakdown, and other channels' own "induced by Media-Driven Brand Equity" sub-bars, keep
    // showing real attribution data instead of being zeroed too.
    if (group.name.toLowerCase() === 'media-driven brand equity') {
      group.totalActualizedSpend = 0;
      group.totalActualizedSpendRawValue = 0;
      group.totalActualizedSpendRawValueLabel = currencyFormatter.format(0);
      group.totalActualizedEffect = 0;
      group.totalActualizedEffectRawValue = 0;
      group.totalActualizedEffectRawValueLabel =
        kpiType === 'revenue' ? currencyFormatter.format(0) : numberFormatter.format(0);
    }

    // Bar-render-only lengths (DEV-24230), for the outline <Bar>'s dataKey only. The rendered
    // bar is a stack (base segment + one sub-bar per contributor) that always sums to gross,
    // regardless of `added` -- so a net-based outline (correct for the numeric label, per
    // DEV-23906) was shorter than its own bars, collapsing to nothing when net is exactly 0
    // (e.g. Media-Driven Brand Equity).
    group.totalActualizedSpendBarValue = group.value + group.contributorsSpendSum;
    group.totalActualizedEffectBarValue = group.value2 + group.contributorsEffectSum;

    return group;
  });

  const finishedObj = {
    barData: resultsToArray,
    titleData: `Total spend to effect ratio 1:${(
      totalEffectAllChannels / totalSpendAllChannels
    ).toFixed(2)}`
  };
  return finishedObj;
};

export const decayDataFormatter = (
  data: DecayData[],
  yAxisDataKey: string,
  disabledPlatform: boolean,
  companyEqualsTest?: boolean
): { newD: DateChartRow[]; lines: AreaItem[]; groupedLines: GroupedAreas[] } => {
  const d = R.groupBy(item => String(item.period), data);
  const newD: DateChartRow[] = [];
  const grouping: Record<string, AreaGrouping>[] = [];

  Object.keys(d).forEach((key, index) => {
    newD[index] = { date: key };
    grouping[index] = {};

    d[key].forEach(item => {
      newD[index][item.name] = (Number(item[yAxisDataKey]) || 0) * 100;

      if (
        (item.platform === null || item.platform === '' || disabledPlatform) &&
        (item.tactic === null || item.tactic === '') &&
        (item.brand_nonbrand === null || item.brand_nonbrand === '')
      ) {
        const targetChannels = ['Shopper', 'Super bowl', 'Influencer']; // HROAS: Micah requested to have "Total" if Channel has no platforms instead of "All [Channel]"
        const isTargetChannel = targetChannels.some(tc =>
          (item.channel || '').toLowerCase().includes(tc.toLowerCase())
        );

        grouping[index][item.name] = {
          channel: capitalizeWords(item.channel, disabledPlatform),
          platform: isTargetChannel
            ? 'Average'
            : `All ${capitalizeWords(item.channel, disabledPlatform)}`,
          tactic: null,
          brand_nonbrand: null
        };
      } else {
        grouping[index][item.name] = {
          channel: capitalizeWords(item.channel, disabledPlatform),
          platform: capitalizeWords(item.platform, disabledPlatform),
          tactic: capitalizeWords(item.tactic, disabledPlatform),
          brand_nonbrand: capitalizeWords(item.brand_nonbrand, disabledPlatform)
        };
      }
    });
  });

  const keys = Object.keys(newD[0]).filter(key => key !== 'date');
  const lines: AreaItem[] = [];
  keys.forEach((key, index) => {
    const incrementalityFind = data.find(item => item.name === key);

    const incrementality = getIncrementality(
      key,
      Boolean(incrementalityFind?.incrementality_test),
      disabledPlatform,
      companyEqualsTest
    );

    lines[index] = {
      name: capitalizeWords(key, disabledPlatform),
      dataKey: key,
      grouping: grouping[0]?.[key] ?? null,
      incrementality
    };
  });

  let groupedLines: GroupedAreas[] = [];
  if (lines.length > 0 && lines[0].grouping) {
    groupedLines = lines.reduce<GroupedAreas[]>((acc, obj) => {
      const { channel } = obj.grouping || { channel: 'unknown' }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  return { newD, lines, groupedLines };
};

interface SaturationLine {
  name: string;
  data: SatData[];
  grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  incrementality: boolean;
}

interface SaturationGroupedLines {
  channel: string;
  areas: SaturationLine[];
}

export const saturationDataFormatter = (
  data: SatData[],
  disabledPlatform: boolean,
  centralMeasure: MedianOrMean,
  companyEqualsTest?: boolean
): {
  newD: SaturationLine[];
  centralMeasures: Record<string, Record<string, number | null>>;
  groupedLines: SaturationGroupedLines[];
} => {
  const d: Record<string, SatData[]> = R.groupBy(item => item.name, data);

  const centralMeasures: Record<string, Record<string, number | null>> = {};
  for (const [key, value] of Object.entries(d)) {
    const centralRow = value.find(row => row[centralMeasure.toLowerCase()] === 1);
    centralMeasures[key] = centralRow
      ? {
          [centralMeasure.toLowerCase()]: centralRow.adstock_spend,
          dataPoint: centralRow.adstock_spend
        }
      : { [centralMeasure.toLowerCase()]: null, dataPoint: null };
  }

  const transformedData: SaturationLine[] = Object.entries(d).map(([name, entries]) => {
    const channel = capitalizeWords(entries[0].channel, disabledPlatform);
    let platform = capitalizeWords(entries[0].platform, disabledPlatform);
    const tactic = capitalizeWords(entries[0].tactic, disabledPlatform);
    const brand_nonbrand = capitalizeWords(entries[0].brand_nonbrand, disabledPlatform);
    if ((!platform || disabledPlatform) && !brand_nonbrand && !tactic) {
      platform = `All ${channel}`;
    }

    const incrementality = getIncrementality(
      name,
      Boolean(entries[0].incrementality_test),
      disabledPlatform,
      companyEqualsTest
    );

    return {
      name: capitalizeWords(name, disabledPlatform),
      data: entries,
      grouping: { channel, platform, tactic, brand_nonbrand },
      incrementality
    };
  });

  let groupedLines: SaturationGroupedLines[] = [];
  if (transformedData.length > 0 && transformedData[0].grouping) {
    groupedLines = transformedData.reduce<SaturationGroupedLines[]>((acc, obj) => {
      const { channel } = obj.grouping || { channel: 'unknown' }; // Default to 'unknown' if channel is not present
      const existingGroup = acc.find(item => item.channel === channel);

      if (existingGroup) {
        existingGroup.areas.push(obj);
      } else {
        acc.push({ channel, areas: [obj] });
      }

      return acc;
    }, []);
  }

  return { newD: transformedData, centralMeasures, groupedLines };
};

interface RoasCell {
  value: string | number | null;
  label: string | number;
}

type RoasDimensionCell = Record<
  string,
  {
    value: string | number | null;
    label: string;
    content?: ReactNode;
  }
>;

// Turns array of objects, where each object is a row in the table,
// into an array of arrays, where each inner array is a row in the table
// each inner array is an array of objects, where each object is a cell in the table
export const roasDataFormatter = ({
  data,
  tableHeaders,
  disabledPlatform,
  companyEqualsTest,
  kpiType
}: {
  data: RoasData[];
  tableHeaders: (Column | DimensionColumn)[];
  disabledPlatform: boolean;
  companyEqualsTest?: boolean;
  kpiType?: string;
}): RoasCell[][] | RoasDimensionCell[] => {
  const newD: RoasCell[][] = [];
  const newDimension: RoasDimensionCell[][] = [];

  data.forEach((item, i) => {
    newD[i] = [];
    newDimension[i] = [];
    tableHeaders.forEach(header => {
      const rawValue = item[header.id];
      const label: string | number | null =
        typeof rawValue === 'number' || rawValue === null ? rawValue : capitalizeWords(rawValue);
      let formattedLabel: string | undefined;

      switch (header.id) {
        case 'next_dollar_effectiveness':
          if (rawValue === 0 && kpiType) {
            formattedLabel = kpiType === 'revenue' ? '$0' : '0';
          } else if (rawValue < 0.01 && kpiType) {
            formattedLabel = kpiType === 'revenue' ? '<$0.01' : '<0.01';
          }
          if (rawValue >= 0.01 && kpiType) {
            formattedLabel =
              kpiType === 'revenue'
                ? formatMoney(Math.abs(rawValue), 2)
                : formatNumber(Math.abs(rawValue), 2);
          }
          break;
        case 'response_previous_qtr_median_spend':
        case 'previous_qtr_median_spend':
        case 'previous_qtr_mean_spend':
        case 'response_previous_qtr_mean_spend':
          if (kpiType === 'revenue') {
            if (rawValue === 0 && kpiType) {
              formattedLabel = '$0';
            } else if (rawValue < 0.5 && kpiType) {
              formattedLabel = '<$1';
            } else if (rawValue < 1 && kpiType) {
              formattedLabel = '$1';
            } else if (rawValue > 1 && kpiType) {
              formattedLabel = formatMoney(rawValue, 0);
            }
            break;
          } else {
            if (rawValue === 0 && kpiType) {
              formattedLabel = '0';
            } else if (rawValue < 0.5 && kpiType) {
              formattedLabel = '<1';
            } else if (rawValue < 1 && kpiType) {
              formattedLabel = '1';
            } else if (rawValue > 1 && kpiType) {
              formattedLabel = formatNumber(rawValue, 0);
            }
            break;
          }
        case 'name':
          formattedLabel = capitalizeWords(rawValue, disabledPlatform).replace(/,/g, ' –');
          break;
        default:
          if (rawValue === 0 && kpiType) {
            formattedLabel = kpiType === 'revenue' ? '0' : '$0';
          } else if (rawValue < 0.01 && kpiType) {
            formattedLabel = kpiType === 'revenue' ? '<0.01' : '<$0.01';
          }
          if (rawValue >= 0.01 && kpiType) {
            const convDecimal = rawValue > 15 ? 0 : 2;
            formattedLabel =
              kpiType === 'revenue'
                ? formatNumber(rawValue, convDecimal)
                : formatMoney(rawValue, convDecimal);
          }
          break;
      }

      const incrementality = getIncrementality(
        item.name,
        Boolean(item.incrementality_test),
        disabledPlatform,
        companyEqualsTest
      );

      if (header.id === 'name' && 'dimensionTypeName' in header) {
        const contentLabel = formattedLabel || formattedLabel === '0' ? formattedLabel : '--';
        newDimension[i].push({
          [header.dimensionTypeName]: {
            value: label,
            label: contentLabel,
            content: incrementality ? (
              <div className='label-dimension-icon-wrapper'>
                {contentLabel}
                <div>
                  <IncrementalityIcon className='incrementality-icon-inline' />
                </div>
              </div>
            ) : undefined
          }
        });
      }

      // Label is the value of the cell; value: header.label is the column name
      const contentLabel = formattedLabel ?? '--';
      newD[i].push({
        value: label,
        label: contentLabel
      });
    });
  });

  if (newDimension[0] && newDimension[0].length > 0) {
    return newDimension.flat();
  }

  return newD;
};

interface RoasSnapshotItem {
  name: string;
  value: number;
  incrementality: boolean;
}

export const roasSnapshotData = (
  data: RoasData[],
  disabledPlatform: boolean,
  companyEqualsTest?: boolean
): RoasSnapshotItem[] =>
  data.map(item => {
    const incrementality = getIncrementality(
      item.name,
      Boolean(item.incrementality_test),
      disabledPlatform,
      companyEqualsTest
    );
    return {
      name: capitalizeWords(item.name, disabledPlatform).replace(/,/g, ' –'),
      value: item.next_dollar_effectiveness,
      incrementality
    };
  });

// Currently only filtering for platform
export const findGroupByOptions = (data: CptbData[]): { label: string; value: string }[] => {
  const filteredData = data.filter(
    item => item.platform !== 'total' && item.platform !== '' && item.platform !== null
  );

  const optionsArray = R.isEmpty(filteredData)
    ? [
        { label: 'Channel', value: 'channel' },
        { label: 'Channel, Tactic, Brand/Nonbrand', value: 'name' }
      ]
    : [
        { label: 'Channel', value: 'channel' },
        { label: 'Channel, Platform', value: 'platform' },
        { label: 'Channel, Platform, Tactic, Brand/Nonbrand', value: 'name' }
      ];

  return optionsArray;
};

export const calculateTTMOverallRoasCpa = (data: CptbData[], kpiType: string): number => {
  let totalSpendSum = 0;
  let totalOutcomeSum = 0;
  data.forEach(row => {
    if (row.name.toLowerCase() !== 'covariates') {
      totalSpendSum += row.raw_spend ?? 0;
      totalOutcomeSum += row.predicted_outcome;
    }
  });

  if (totalOutcomeSum === 0 || totalSpendSum === 0) {
    return 0;
  }

  return kpiType === 'revenue' ? totalOutcomeSum / totalSpendSum : totalSpendSum / totalOutcomeSum;
};
